/* eslint-disable react/prop-types */
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./dialog";
import { Button } from "./button";
import { X, Check } from "lucide-react";


const SIZE_MAP = {
  sm: "max-w-md",
  md: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-4xl",
  "2xl": "max-w-5xl",
};


/**
 * MobileFormDialog — wrapper opinativo para formulários longos.
 *
 * Aplica automaticamente:
 *   - w-[95vw] max-h-[90vh] overflow-y-auto overflow-x-hidden (mobile-safe)
 *   - Sticky header com botão Cancelar (X) + Guardar (Check) — alcançável com polegar
 *   - space-y-4 consistente entre secções
 *
 * Quando usar:
 *   ✓ Formulários multi-secção com 5+ campos
 *   ✓ Diálogos que precisam de funcionar bem em mobile (95% dos casos da DI)
 *
 * Quando NOT usar:
 *   ✗ Dialogs de confirmação simples (use <Dialog> + <AlertDialog>)
 *   ✗ Modais com tabelas largas onde scroll horizontal é desejável
 *
 * Props:
 *   open: boolean
 *   onOpenChange: (boolean) => void
 *   title: string
 *   description?: string
 *   size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' (default 'lg')
 *   onSubmit?: () => void | Promise<void>
 *   submitLabel?: string (default 'Guardar')
 *   submitting?: boolean
 *   submitDisabled?: boolean
 *   showSubmit?: boolean (default true)
 *   testId?: string
 *   children: ReactNode
 */
export default function MobileFormDialog({
  open,
  onOpenChange,
  title,
  description,
  size = "lg",
  onSubmit,
  submitLabel = "Guardar",
  submitting = false,
  submitDisabled = false,
  showSubmit = true,
  testId,
  children,
}) {
  const sizeCls = SIZE_MAP[size] || SIZE_MAP.lg;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`bg-slate-900 border-slate-700 w-[95vw] ${sizeCls} max-h-[90vh] overflow-y-auto overflow-x-hidden p-0 grid-cols-[minmax(0,1fr)]`}
        data-testid={testId}
      >
        {/* Sticky header — botões alcançáveis com polegar em mobile */}
        <div className="sticky top-0 z-10 bg-slate-900/95 backdrop-blur border-b border-slate-700 px-4 py-3 flex items-start justify-between gap-2 min-w-0">
          <div className="flex-1 min-w-0">
            <DialogTitle className="text-white text-sm font-semibold truncate">
              {title}
            </DialogTitle>
            {description && (
              <DialogDescription className="text-slate-400 text-[11px] mt-0.5 line-clamp-2">
                {description}
              </DialogDescription>
            )}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange?.(false)}
              disabled={submitting}
              className="h-8 px-2 text-slate-400 hover:text-white hover:bg-slate-800"
              data-testid={testId ? `${testId}-cancel` : undefined}
              title="Cancelar"
            >
              <X className="w-4 h-4" />
            </Button>
            {showSubmit && onSubmit && (
              <Button
                size="sm"
                onClick={onSubmit}
                disabled={submitDisabled || submitting}
                className="h-8 bg-blue-600 hover:bg-blue-500 text-white"
                data-testid={testId ? `${testId}-submit` : undefined}
              >
                <Check className="w-4 h-4 mr-1" />
                {submitting ? "A guardar…" : submitLabel}
              </Button>
            )}
          </div>
        </div>

        {/* Conteúdo — secções em space-y-4 */}
        <div className="px-4 py-4 space-y-4 text-xs min-w-0">
          {children}
        </div>
      </DialogContent>
    </Dialog>
  );
}
